import { useEffect } from 'react';

interface SeoPageOptions {
  title: string;
  description: string;
  keywords: string;
  canonicalPath?: string;
  ogImage?: string;
  jsonLd?: object | object[];
}

const DEFAULT_OG_IMAGE = 'https://static.readdy.ai/image/4f8e52dde3dda7b1227768a95bdc6f97/4595d76c80b5322e477f2894d2f3301b.png';

const useSeoPage = ({ title, description, keywords, canonicalPath, ogImage, jsonLd }: SeoPageOptions) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const getOrCreate = (selector: string, tag: string, attrs: Record<string, string>): Element => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(tag);
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
        document.head.appendChild(el);
      }
      return el;
    };

    const descEl = getOrCreate('meta[name="description"]', 'meta', { name: 'description', content: '' });
    const prevDesc = descEl.getAttribute('content') || '';
    descEl.setAttribute('content', description);

    const kwEl = getOrCreate('meta[name="keywords"]', 'meta', { name: 'keywords', content: '' });
    const prevKw = kwEl.getAttribute('content') || '';
    kwEl.setAttribute('content', keywords);

    // Last-Modified
    const lastModEl = getOrCreate('meta[name="last-modified"]', 'meta', { name: 'last-modified', content: '' });
    const prevLastMod = lastModEl.getAttribute('content') || '';
    lastModEl.setAttribute('content', new Date().toISOString());

    // Open Graph
    const ogTitleEl = getOrCreate('meta[property="og:title"]', 'meta', { property: 'og:title', content: '' });
    const prevOgTitle = ogTitleEl.getAttribute('content') || '';
    ogTitleEl.setAttribute('content', title);

    const ogDescEl = getOrCreate('meta[property="og:description"]', 'meta', { property: 'og:description', content: '' });
    const prevOgDesc = ogDescEl.getAttribute('content') || '';
    ogDescEl.setAttribute('content', description);

    const imageUrl = ogImage || DEFAULT_OG_IMAGE;
    const ogImageEl = getOrCreate('meta[property="og:image"]', 'meta', { property: 'og:image', content: '' });
    const prevOgImage = ogImageEl.getAttribute('content') || '';
    ogImageEl.setAttribute('content', imageUrl);

    const ogSiteNameEl = getOrCreate('meta[property="og:site_name"]', 'meta', { property: 'og:site_name', content: '' });
    const prevOgSiteName = ogSiteNameEl.getAttribute('content') || '';
    ogSiteNameEl.setAttribute('content', 'Zimbet32');

    const ogLocaleEl = getOrCreate('meta[property="og:locale"]', 'meta', { property: 'og:locale', content: '' });
    const prevOgLocale = ogLocaleEl.getAttribute('content') || '';
    ogLocaleEl.setAttribute('content', 'ru_MD');

    // Twitter Card
    const twCardEl = getOrCreate('meta[name="twitter:card"]', 'meta', { name: 'twitter:card', content: '' });
    const prevTwCard = twCardEl.getAttribute('content') || '';
    twCardEl.setAttribute('content', 'summary_large_image');

    const twTitleEl = getOrCreate('meta[name="twitter:title"]', 'meta', { name: 'twitter:title', content: '' });
    const prevTwTitle = twTitleEl.getAttribute('content') || '';
    twTitleEl.setAttribute('content', title);

    const twDescEl = getOrCreate('meta[name="twitter:description"]', 'meta', { name: 'twitter:description', content: '' });
    const prevTwDesc = twDescEl.getAttribute('content') || '';
    twDescEl.setAttribute('content', description);

    const twImageEl = getOrCreate('meta[name="twitter:image"]', 'meta', { name: 'twitter:image', content: '' });
    const prevTwImage = twImageEl.getAttribute('content') || '';
    twImageEl.setAttribute('content', imageUrl);

    // Canonical & og:url
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    const canonicalHref = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;
    const canonicalEl = getOrCreate('link[rel="canonical"]', 'link', { rel: 'canonical', href: '' });
    const prevCanonical = canonicalEl.getAttribute('href') || '';
    canonicalEl.setAttribute('href', canonicalHref);

    const ogUrlEl = getOrCreate('meta[property="og:url"]', 'meta', { property: 'og:url', content: '' });
    const prevOgUrl = ogUrlEl.getAttribute('content') || '';
    ogUrlEl.setAttribute('content', canonicalHref);

    // JSON-LD
    const scripts: HTMLScriptElement[] = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((data) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(data);
        script.setAttribute('data-seo-page', 'true');
        document.head.appendChild(script);
        scripts.push(script);
      });
    }

    return () => {
      document.title = prevTitle;
      descEl.setAttribute('content', prevDesc);
      kwEl.setAttribute('content', prevKw);
      lastModEl.setAttribute('content', prevLastMod);
      ogTitleEl.setAttribute('content', prevOgTitle);
      ogDescEl.setAttribute('content', prevOgDesc);
      ogImageEl.setAttribute('content', prevOgImage);
      ogSiteNameEl.setAttribute('content', prevOgSiteName);
      ogLocaleEl.setAttribute('content', prevOgLocale);
      twCardEl.setAttribute('content', prevTwCard);
      twTitleEl.setAttribute('content', prevTwTitle);
      twDescEl.setAttribute('content', prevTwDesc);
      twImageEl.setAttribute('content', prevTwImage);
      canonicalEl.setAttribute('href', prevCanonical);
      ogUrlEl.setAttribute('content', prevOgUrl);
      scripts.forEach((s) => {
        if (document.head.contains(s)) document.head.removeChild(s);
      });
    };
  }, [title, description, keywords, canonicalPath, ogImage]);
};

export default useSeoPage;
